import { useMemo, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Button, Card, CardContent, Divider, IconButton, Typography } from "@mui/material";

import { ApiCreditCardsResponse, CreditCardItemType } from "@/api/creditCard";

import "./styles.scss";

interface CreditCardItemProps {
  data: CreditCardItemType;
  categories: ApiCreditCardsResponse["categories"];
  isDetail: boolean;
}

function replaceStyle(html: string) {
  return html.replace(/(\s)style="\S*?"/g, "");
}

const CreditCardItem = (props: CreditCardItemProps) => {
  const { data, categories, isDetail = false } = props;

  const [open, setOpen] = useState(isDetail ? true : false);

  const detailIntro = useMemo(() => {
    return (
      data.detail?.replace(/<[^<>]+>/g, "")?.slice(0, 450) +
      "<span class='text-[#90caf9] cursor-pointer'>...read more</span>"
    );
  }, [data.detail]);

  return (
    <Card className="creditCardItem" elevation={6} sx={{ borderRadius: "15px", padding: "7px", marginBottom: "45px !important" }}>
      <CardContent>
        {data.tag_line && (
          <Typography fontSize={16} color="primary">
            {data.tag_line}
          </Typography>
        )}
        <Typography fontSize={18} fontWeight="bold">
          {data.name}
        </Typography>
        <div className="flex mt-[10px] flex-col md:flex-row">
          <div className="flex items-center justify-center">
            <img src={data.img} className="w-full md:w-[200px] h-auto rounded-md object-contain" />
          </div>
          <div style={{ marginLeft: "20px" }}>
            <Typography fontSize={20} color="primary" fontWeight="bold">
              Rewards
            </Typography>
            {data.benefit?.map((item) => (
              <div key={item.bold}>
                <Typography>
                  <div className="flex items-center">
                    <span
                      style={{
                        fontWeight: "bold",
                        fontSize: "1.3em",
                        minWidth: "55px",
                        width: "40px",
                        display: "inline-block",
                        textAlign: "center",
                      }}
                    >
                      {item.bold}
                    </span>
                    <span style={{ fontSize: "1.1em", marginLeft: "5px" }}>{item.value}</span>
                  </div>
                </Typography>
              </div>
            ))}
          </div>
          {!isDetail && (
            <div className="flex justify-center mt-[10px] ml-[45px] ml-auto">
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  window.open(`/credit-card/${data.id}`, "_blank");
                  // window.open(data.link || "https://www.awardtool.com", "_blank");
                }}
                rel="noopener noreferrer"
                className="w-full md:w-[150px]"
                style={{
                  margin: "auto 0",
                  display: "flex",
                  alignItems: "center",
                  height: "36px",
                  borderRadius: "10px",
                }}
              >
                <LockOpenIcon sx={{ fontSize: 16, marginRight: 1, }} /> Learn more
              </Button>
            </div>
          )}

          <div />
        </div>
        <div className="md:ml-[10px] flex-1 mt-[20px] mb-[15px]" style={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '8px', paddingLeft: '20px', paddingRight: '20px', paddingTop: '12px' }}>
          {/* <Chip label="Bonus Amount" color="primary" /> */}
          {/* <Typography fontSize={16} fontWeight="bold">Intro Offer</Typography>
            <Typography className="!mt-[10px]">Earn $900 bonus cash back</Typography> */}
          <div className="flex flex-col flex-1 mt-[0px]">
            <Typography color="primary" fontWeight="bold">
              Intro Offer
            </Typography>
            <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>{data?.intro_offer}</span>
            <span style={{  }}>{data?.spending_requirement_information}</span>  
          </div>
          <div className="flex flex-wrap justify-between my-[10px]">
            <div className="flex flex-col flex-1">
              {/* <span className="text-[#999]">Spending Requirement</span> */}
              <Typography color="primary" fontWeight="bold">
                Spending Requirement
              </Typography>
              <span>
                {Array.isArray(data.spending_requirement) 
                  ? `$${data.spending_requirement.map(amount => amount.toLocaleString()).join(' / $')}`
                  : data.spending_requirement.toString().startsWith('$') 
                    ? data.spending_requirement.toLocaleString() 
                    : `$${data.spending_requirement.toLocaleString()}`
                }
              </span>
            </div>

            <div className="flex flex-col flex-1">
              {/* <span className="text-[#999]">Annual Fee</span> */}
              <Typography color="primary" fontWeight="bold">
                Annual Fee
              </Typography>
              <span>${data.annual_fee.toLocaleString()}</span>
            </div>

            <div className="flex flex-col flex-1">
              {/* <span className="text-[#999]">Credit Score</span> */}
              <Typography color="primary" fontWeight="bold">
                Credit Score
              </Typography>
              <span>{data?.credit_requirement?.score}</span>
              <span>{data?.credit_requirement?.category}</span>
            </div>
            <div className="flex flex-col flex-1">
              {/* <span className="text-[#999]">Category</span> */}
              <Typography color="primary" fontWeight="bold">
                Category
              </Typography>
              <span>
                {categories
                  .filter((item) => data.categories.includes(item.id))
                  .map((item) => item.name)
                  .join(", ")}
              </span>
            </div>
          </div>
        </div>
        {/* <Divider /> */}
        <div>
          <div className="w-full md:w-[300px] flex items-center justify-between mt-[-0px]">
            <Typography fontSize={12}>Terms Applied</Typography>
          </div>
          <div
            className="pb-[10px]"
            dangerouslySetInnerHTML={{ __html: replaceStyle(data.summary) }}
          />
        </div>
        <Divider />
        <div className="pt-[10px]">
          <div className="flex items-center space-x-[4px]">
            <Typography fontSize={18} fontWeight="bold">
              Our comments
            </Typography>
            <IconButton aria-label="delete" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </div>

          <div
            onClick={() => {
              if (!open) {
                setOpen(true);
              }
            }}
            dangerouslySetInnerHTML={{ __html: open ? replaceStyle(data.detail) : detailIntro }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
export default CreditCardItem;
